import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { IncompleteReason } from "@heraion/shared";

import type { TFn } from "../i18nTypes";
import type { UiTheme } from "../theme";
import { hiddenFromScreenReader } from "./hiddenFromScreenReader";
import { InfoPill, InfoPillBody } from "./InfoPill";

// <IncompleteNote>: a row says it may undercount, with the reason(s): an
// exercise logged in the window is not in the catalog, so its sets credit no
// muscle; or a session was saved with no bodyweight on the profile, so its
// bodyweight sets counted zero (#116). No `reasons` = the catalog one.
// Glyph + word (never colour-only) and an (i) pill with the reason. The pill
// state lives here; mount this OUTSIDE any `accessible` node, or the Pressable
// is swallowed by it. Pair the row's spoken label with `incompleteWord`.

export const INCOMPLETE_GLYPH = "≥";

export const incompleteWord = (t: TFn): string =>
  t("mobile.home.targetsIncomplete", { defaultValue: "incomplete" });

// One sentence per reason, then the shared closing ONCE (two reasons used to
// say "may be higher" twice).
const whyText = (t: TFn, reason: IncompleteReason): string =>
  reason === "no-bodyweight"
    ? t("mobile.home.targetsIncompleteNoBodyweight", {
        defaultValue:
          "A session in the last 7 days was saved before your bodyweight was on your profile, so its bodyweight sets count as zero. The admin recomputes it: you don't need to do anything.",
      })
    : t("mobile.home.targetsIncompleteWhy", {
        defaultValue:
          "An exercise logged in the last 7 days is not in the catalog, so its sets count toward no muscle.",
      });

const whyBody = (t: TFn, reasons: readonly IncompleteReason[]): string =>
  [
    ...reasons.map((r) => whyText(t, r)),
    t("mobile.home.targetsIncompleteMayBeHigher", { defaultValue: "The real number may be higher." }),
  ].join(" ");

export function IncompleteNote({
  theme,
  reasons = ["uncatalogued"],
}: {
  theme: UiTheme;
  reasons?: readonly IncompleteReason[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const styles = makeStyles(theme);
  return (
    <View>
      <View style={styles.line}>
        <Text style={styles.word} {...hiddenFromScreenReader()}>
          {INCOMPLETE_GLYPH} {incompleteWord(t)}
        </Text>
        <InfoPill
          theme={theme}
          open={open}
          onToggle={() => setOpen((o) => !o)}
          a11yLabel={t("mobile.home.targetsIncompleteA11y", {
            defaultValue: "Why incomplete",
          })}
        />
      </View>
      {open ? (
        <InfoPillBody theme={theme} text={whyBody(t, reasons)} />
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    line: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      marginTop: s.s1,
    },
    word: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600" },
  });
}
